import type { InferInput } from 'valibot'
import type { User } from './auth'
import type { SchemaBase } from './base'
import { any, array, email, intersect, length, literal, maxLength, maxValue, minLength, minValue, nullable, number, object, optional, partial, picklist, pipe, string, transform, union, undefined as v_undefined, ValiError } from 'valibot'

export const supportedLanguages = ['english', 'hindi', 'bengali', 'kannada', 'marathi', 'tamil', 'telugu'] as const
export type SupportedLanguage = typeof supportedLanguages[number]

export const consentFormTemplateFormats = ['pdf', 'audio', 'video'] as const
export type ConsentFormTemplateFormat = typeof consentFormTemplateFormats[number]

export const userTypes = ['patient', 'doctor', 'care'] as const
export type UserType = typeof userTypes[number]

export const assetTypes = ['blog', 'poster', 'video', 'pdf', 'audio'] as const
export type AssetType = typeof assetTypes[number]

export const publishStatuses = ['draft', 'published'] as const
export type PublishStatus = typeof publishStatuses[number]

function checkObjectId(msgName: string) {
  // just check for string: no need to check for objectId on the client side
  return string(`${msgName} ID is required`)
}

/**
 * Procedures
 */
export const createProcedureSchema = object({
  name: pipe(string('Name is required'), minLength(3, 'Name must be atleast 3 characters'), maxLength(50, 'Name must be less than 50 characters')),
  category: checkObjectId('Category'),
})
export type CreateProcedureSchema = InferInput<typeof createProcedureSchema>

export type Procedure = SchemaBase & CreateProcedureSchema & {
  createdBy: string | User
}

export type BaseProcedure = Omit<Procedure, 'createdAt' | 'updatedAt' | 'createdBy' | 'category'>

export const deleteProcedureSchema = object({
  procedureId: checkObjectId('Procedure'),
})
export type DeleteProcedureSchema = InferInput<typeof deleteProcedureSchema>

export const updateProcedureSchema = intersect ([deleteProcedureSchema, partial(createProcedureSchema)])
export type UpdateProcedureSchema = InferInput<typeof updateProcedureSchema>

export const getEducationalMaterialsByProcedureIdSchema = object({
  procedureId: union([literal('all'), checkObjectId('Procedure')]),
  language: optional (picklist(supportedLanguages, 'Unsupported language')),
})
export type GetEducationalMaterialsByProcedureIdSchema = InferInput<typeof getEducationalMaterialsByProcedureIdSchema>

export const getEducationalMaterialsByCategoryIdSchema = object({
  categoryId: union([literal('all'), checkObjectId('Category')]),
  page: number(),
  language: picklist(supportedLanguages, 'Unsupported language'),
})
export type GetEducationalMaterialsByCategoryIdSchema = InferInput<typeof getEducationalMaterialsByCategoryIdSchema>

/**
 * Categories
 */
export const createCategorySchema = object({
  name: pipe(string('Name is required'), minLength(3, 'Name must be atleast 3 characters'), maxLength(50, 'Name must be less than 50 characters')),
})
export type CreateCategorySchema = InferInput<typeof createCategorySchema>

export type Category = CreateCategorySchema & {
  _id: string
}

export interface GetEducationalMaterialsByProcedureIdResponseType {
  [procedureId: string]: {
    name: string
    categoryName: string
    educationalMaterials: Array<Omit<EducationalMaterial, 'procedure'>>
  }
}

export const imageFileTypes = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  'image/svg+xml',
] as const

export const audioFileTypes = [
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'audio/flac',
  'audio/aac',
  'audio/mp3',
  'audio/mp4',
  'audio/webm',
  'audio/x-ms-wma',
] as const

export const pdfFileTypes = [
  'application/pdf',
] as const

export const videoFileTypes = [
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime',
] as const

export const allowedFileTypes = [
  ...imageFileTypes,
  ...audioFileTypes,
  ...pdfFileTypes,
  ...videoFileTypes,
] as const

export function getAssetTypeFromFileType(fileType: string): AssetType | null {
  if (pdfFileTypes.includes(fileType as any)) return 'pdf'
  if (videoFileTypes.includes(fileType as any)) return 'video'
  if (imageFileTypes.includes(fileType as any)) return 'poster'
  if (audioFileTypes.includes(fileType as any)) return 'audio'
  return null
}

export const createConsentFormSchema = object({
  url: pipe(string('URL is required'), minLength(10, 'URL must be atleast 3 characters')),
  procedureId: checkObjectId('Procedure'),
  format: picklist(consentFormTemplateFormats, 'Unsupported format'),
  language: picklist(supportedLanguages, 'Unsupported language'),
})
export type CreateConsentFormSchema = InferInput<typeof createConsentFormSchema>

export type ConsentForm = SchemaBase & Omit<CreateConsentFormSchema, 'procedureId'> & {
  createdBy: string | User
  procedure: string | Procedure
  status: PublishStatus
}

export const deleteConsentFormSchema = object({
  consentFormId: checkObjectId('Consent Form'),
})
export type DeleteConsentFormSchema = InferInput<typeof deleteConsentFormSchema>

export const updateConsentFormSchema = intersect([
  deleteConsentFormSchema,
  partial(createConsentFormSchema),
  object({ status: optional(picklist(publishStatuses, 'Unsupported status')) }),
])
export type UpdateConsentFormSchema = InferInput<typeof updateConsentFormSchema>

export const createEducationalMaterialSchema = object({
  title: optional(string('Title must be a string')),
  procedureId: checkObjectId('Procedure'),
  assetUrl: string(),
  thumbnailUrl: optional(string()),
  content: optional(string()),
  description: optional(string()),
  language: picklist(supportedLanguages, 'Unsupported language'),
  assetType: picklist(assetTypes, 'Unsupported asset type'),
})
export type CreateEducationalMaterialSchema = InferInput<typeof createEducationalMaterialSchema>

export type EducationalMaterial = SchemaBase & Omit<CreateEducationalMaterialSchema, 'procedureId'> & {
  createdBy: string | User
  procedure: string | Procedure | BaseProcedure
  status: PublishStatus
  toc?: Array<{ level: number, text: string, _id: string }>
}

export const deleteEducationalMaterialSchema = object({
  educationalMaterialId: checkObjectId('Educational Material'),
})
export type DeleteEducationalMaterialSchema = InferInput<typeof deleteEducationalMaterialSchema>

export const updateEducationalMaterialSchema = intersect([
  deleteEducationalMaterialSchema,
  partial(createEducationalMaterialSchema),
  object({ status: optional(picklist(publishStatuses, 'Unsupported status')),
  }),
])
export type UpdateEducationalMaterialSchema = InferInput<typeof updateEducationalMaterialSchema>

export const searchEducationalMaterialByProcedureNameSchema = object({
  text: string('Text is required'),
  language: picklist(supportedLanguages, 'Unsupported language'),
})
export type SearchEducationalMaterialByProcedureNameSchema =
  InferInput<typeof searchEducationalMaterialByProcedureNameSchema>

const ONE_MEGABYTE = 1024 * 1024
export const getPresignedUrlSchema = object({
  fileSize: pipe(number('File size is required'), minValue(1024, 'File size must be atleast 1 KiloBytes'), maxValue(50 * ONE_MEGABYTE, 'File size must be less than 30MB')),
  fileType: picklist(allowedFileTypes, 'Unsupported file type'),
  fileChecksum: string('File checksum is required'),
})
export type GetPresignedUrlSchema = InferInput<typeof getPresignedUrlSchema>

export const personalInformationSchema = object({
  userType: picklist(userTypes, 'Invalid user type'),
  fullName: pipe(string('Your name is required'), minLength(3, 'Name must be atleast 3 characters long'), maxLength(255, 'Name must be less than 255 characters long')),
  institutionName: optional(pipe(string('Hospital name is required'), minLength(3, 'Name must be atleast 3 characters long'), maxLength(255, 'Name must be less than 255 characters long'))),
  phone: pipe(string('Phone number is required'), length(10, 'Phone number must be 10 digits long'), transform((val) => {
    if (Number.isNaN(Number(val))) {
      throw new ValiError([{ message: 'Invalid phone number', expected: 'number', input: val, kind: 'validation', received: val, type: 'number' }])
    }
    return val
  })),
  email: union([v_undefined(), literal(''), pipe(string('Email is required'), email('Invalid email'))]),
})
export type PersonalInformationType = InferInput<typeof personalInformationSchema>
export const defaultPersonalInformationData: Partial<PersonalInformationType> = {}

export const documentTypes = ['consent', 'sop'] as const
export type DocumentType = typeof documentTypes[number]

export const procedureDetailsSchema = object({
  procedure: string('Procedure is required'),
  documentType: optional(picklist(documentTypes, 'Unsupported document type'), 'consent'),
  language: picklist(supportedLanguages, 'Unsupported language'),
  format: picklist(consentFormTemplateFormats, 'Unsupported format'),
})

export type ProcedureDetailsType = InferInput<typeof procedureDetailsSchema>
export const defaultProcedureDetailsData: Partial<ProcedureDetailsType> = {
  procedure: '',
  documentType: 'consent',
}

export const shareFeedbackSchema = object({
  comment: optional(string()),
  expScore: optional(pipe(number(), minValue(1), maxValue(5))),
  procedureDetails: optional(nullable (partial(procedureDetailsSchema))),
  files: array(string()),
})
export type ShareFeedbackType = InferInput<typeof shareFeedbackSchema>

export const downloadConsentFormSchema = procedureDetailsSchema
export type DownloadConsentFormSchema = InferInput<typeof downloadConsentFormSchema>

export const getSearchResultForProcedureNamesSchema = object({
  text: string('Text is required'),
})
export type GetSearchResultForProcedureNamesSchema = InferInput<typeof getSearchResultForProcedureNamesSchema>

export const createLogCheckpointSchema = object({
  step: number('Step is required'),
  userId: string('User ID is required'),
  data: any(),
})
export type CreateLogCheckpointSchema = InferInput<typeof createLogCheckpointSchema>

export type Checkpoint = CreateLogCheckpointSchema & {
  _id: string
  time: Date
}

export const checkpointEventSequence = [
  'Submit Personal Information Form',
  'Submit procedure Details Form',
  'Review/Download Consent Form',
  'Submit share Feedback Form',
  'Start New Form Download Process',
]
